import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Button, Alert, Chip, Divider, MenuItem, TextField, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress
} from '@mui/material';
import { ArrowBack, ListAlt, Add, AccountBalance, ForwardToInbox } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { useLeaveTypes } from '../hooks/useLeaveTypes';
import { locale } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const ENTRY_COLOR = {
  'Allocation':        'success',
  'Leave Taken':       'error',
  'Adjustment':        'warning',
  'Carry Forward':     'info',
  'Leave Recall':      'primary',
  'Forfeiture':        'default',
  'Leave Encashment':  'secondary',
};

const fmt = (d) => d
  ? new Date(d).toLocaleDateString(locale(), { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

const LeaveLedgerPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { leaveTypes } = useLeaveTypes();

  const [entries,    setEntries]    = useState([]);
  const [balances,   setBalances]   = useState([]);
  const [employees,  setEmployees]  = useState([]);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [loading,    setLoading]    = useState(false);

  const [filterEmp,   setFilterEmp]   = useState(searchParams.get('employeeNo')    || '');
  const [filterType,  setFilterType]  = useState(searchParams.get('leaveTypeCode') || '');
  const [filterYear,  setFilterYear]  = useState(new Date().getFullYear().toString());

  // Allocate dialog
  const [allocDlg,    setAllocDlg]    = useState(false);
  const [allocYear,   setAllocYear]   = useState(new Date().getFullYear().toString());
  const [allocating,  setAllocating]  = useState(false);

  // Carry forward dialog
  const [cfDlg,       setCfDlg]       = useState(false);
  const [cfFrom,      setCfFrom]      = useState((new Date().getFullYear() - 1).toString());
  const [cfTo,        setCfTo]        = useState(new Date().getFullYear().toString());
  const [cfRunning,   setCfRunning]   = useState(false);

  // Adjustment dialog
  const [adjDlg,      setAdjDlg]      = useState(false);
  const [adjForm,     setAdjForm]     = useState({ employeeNo: '', leaveTypeCode: '', days: '', academicYear: new Date().getFullYear().toString(), description: '' });
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: adjDlg, value: adjForm, onClose: () => setAdjDlg(false) });
  const [adjSaving,   setAdjSaving]   = useState(false);
  const [adjErr,      setAdjErr]      = useState('');

  useEffect(() => {
    axios.get(`${API}/api/employees`, { headers: hdrs() })
      .then(r => setEmployees(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterEmp)  params.set('employeeNo', filterEmp);
      if (filterType) params.set('leaveTypeCode', filterType);
      if (filterYear) params.set('year', filterYear);
      const { data } = await axios.get(`${API}/api/leave/ledger?${params}`, { headers: hdrs() });
      setEntries(data);

      // Also fetch balances if employee + year selected
      if (filterEmp && filterYear) {
        const balRes = await axios.get(`${API}/api/leave/balance/${filterEmp}?year=${filterYear}`, { headers: hdrs() });
        setBalances(balRes.data || []);
      } else {
        setBalances([]);
      }
    } catch { setError('Failed to load ledger'); }
    finally { setLoading(false); }
  }, [filterEmp, filterType, filterYear]);

  useEffect(() => { load(); }, [load]);

  const years = [];
  for (let y = new Date().getFullYear() + 1; y >= 2022; y--) years.push(y.toString());

  const handleAllocate = async () => {
    setAllocating(true);
    try {
      const { data } = await axios.post(`${API}/api/leave/ledger/allocate`, { academicYear: allocYear }, { headers: hdrs() });
      setSuccess(`Allocated ${data.allocated} entries. Skipped: ${data.skipped}.`);
      setAllocDlg(false); setTimeout(() => setSuccess(''), 5000); load();
    } catch (err) { setError(err.response?.data?.error || 'Allocation failed'); }
    finally { setAllocating(false); }
  };

  const handleCarryForward = async () => {
    setCfRunning(true);
    try {
      const { data } = await axios.post(`${API}/api/leave/ledger/carry-forward`, { fromYear: cfFrom, toYear: cfTo }, { headers: hdrs() });
      setSuccess(`Carry forward complete — ${data.carriedForward} entries, ${data.forfeited} forfeits.`);
      setCfDlg(false); setTimeout(() => setSuccess(''), 5000); load();
    } catch (err) { setError(err.response?.data?.error || 'Carry forward failed'); }
    finally { setCfRunning(false); }
  };

  const handleAdjustment = async () => {
    if (!adjForm.employeeNo || !adjForm.leaveTypeCode || adjForm.days === '') {
      setAdjErr('Employee, Leave Type and Days are required'); return;
    }
    setAdjSaving(true); setAdjErr('');
    try {
      await axios.post(`${API}/api/leave/ledger/adjustment`, {
        ...adjForm, days: Number(adjForm.days),
      }, { headers: hdrs() });
      setSuccess('Adjustment posted');
      setAdjDlg(false); setAdjForm({ employeeNo: '', leaveTypeCode: '', days: '', academicYear: new Date().getFullYear().toString(), description: '' });
      setTimeout(() => setSuccess(''), 3000); load();
    } catch (err) { setAdjErr(err.response?.data?.error || 'Adjustment failed'); }
    finally { setAdjSaving(false); }
  };

  // Running balance per EMPLOYEE + leave type, accumulated oldest → newest.
  // Entries arrive newest-first, so reverse to accumulate chronologically then
  // flip back for display. Keying by employee too is essential: in the
  // "All Employees" view a type's entries from different people are interleaved,
  // and keying by leave type alone would sum them all together (wrong balance).
  const runningBalances = {};
  const enriched = [...entries].reverse().map(e => {
    const key = `${e.employeeNo}|${e.leaveTypeCode}`;
    runningBalances[key] = (runningBalances[key] || 0) + e.days;
    return { ...e, runningBalance: runningBalances[key] };
  }).reverse();

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Button startIcon={<ArrowBack />} size="small" onClick={() => navigate('/hr/leave')}>Back</Button>
              <ListAlt sx={{ color: '#2e7d32' }} />
              <Typography variant="h5" fontWeight={700} color="#2e7d32">Leave Ledger</Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button variant="outlined" size="small" startIcon={<Add />}
                onClick={() => setAdjDlg(true)} color="warning">
                Adjustment
              </Button>
              <Button variant="outlined" size="small" startIcon={<ForwardToInbox />}
                onClick={() => setCfDlg(true)} color="info">
                Carry Forward
              </Button>
              <Button variant="contained" size="small" startIcon={<AccountBalance />}
                onClick={() => setAllocDlg(true)}
                sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
                Allocate
              </Button>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          {/* Filters */}
          <Box display="flex" gap={1.5} mb={2} flexWrap="wrap">
            <TextField select label="Employee" value={filterEmp} size="small" sx={{ minWidth: 220 }}
              onChange={e => setFilterEmp(e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}>
              <MenuItem value="">All Employees</MenuItem>
              {employees.map(e => (
                <MenuItem key={e.employeeNo} value={e.employeeNo}>
                  {e.firstName} {e.lastName} ({e.employeeNo})
                </MenuItem>
              ))}
            </TextField>
            <TextField select label="Leave Type" value={filterType} size="small" sx={{ minWidth: 180 }}
              onChange={e => setFilterType(e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}>
              <MenuItem value="">All Types</MenuItem>
              {leaveTypes.map(lt => <MenuItem key={lt.code} value={lt.code}>{lt.name}</MenuItem>)}
            </TextField>
            <TextField select label="Year" value={filterYear} size="small" sx={{ minWidth: 100 }}
              onChange={e => setFilterYear(e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}>
              <MenuItem value="">All Years</MenuItem>
              {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
          </Box>

          {/* Balance summary cards (only when employee + year selected) */}
          {balances.length > 0 && (
            <Box mb={2}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase">
                {filterYear} Balances
              </Typography>
              <Box display="flex" gap={1.5} mt={1} flexWrap="wrap">
                {balances.map(b => (
                  <Box key={b.leaveTypeCode} sx={{
                    px: 2, py: 1, borderRadius: 2, border: '1px solid',
                    borderColor: b.balance > 0 ? 'success.light' : b.balance < 0 ? 'error.light' : 'divider',
                    bgcolor: b.balance > 0 ? '#e8f5e9' : b.balance < 0 ? '#ffebee' : 'grey.50',
                  }}>
                    <Typography variant="caption" color="text.secondary">{b.leaveTypeName}</Typography>
                    <Typography variant="h6" fontWeight={800}
                      color={b.balance > 0 ? 'success.main' : b.balance < 0 ? 'error.main' : 'text.secondary'}>
                      {b.balance}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Divider sx={{ mb: 2 }} />

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Employee</strong></TableCell>
                <TableCell><strong>Leave Type</strong></TableCell>
                <TableCell align="center"><strong>Entry Type</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell><strong>Application</strong></TableCell>
                <TableCell align="right"><strong>Days</strong></TableCell>
                <TableCell align="right"><strong>Balance</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={24} sx={{ my: 2 }} /></TableCell></TableRow>
              ) : enriched.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" py={3}>No ledger entries found.</Typography>
                  </TableCell>
                </TableRow>
              ) : enriched.map(e => (
                <TableRow key={e._id} hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                  <TableCell><Typography variant="caption">{fmt(e.postingDate)}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{e.employeeName}</Typography>
                    <Typography variant="caption" color="text.secondary">{e.employeeNo}</Typography>
                  </TableCell>
                  <TableCell><Typography variant="caption">{e.leaveTypeName}</Typography></TableCell>
                  <TableCell align="center">
                    <Chip label={e.entryType} size="small" color={ENTRY_COLOR[e.entryType] || 'default'} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{e.description}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace">{e.applicationNo || '—'}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={700}
                      color={e.days > 0 ? 'success.main' : e.days < 0 ? 'error.main' : 'text.secondary'}>
                      {e.days > 0 ? `+${e.days}` : e.days}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}
                      color={e.runningBalance > 0 ? 'success.main' : e.runningBalance < 0 ? 'error.main' : 'text.secondary'}>
                      {e.runningBalance}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── Allocate dialog ── */}
      <Dialog open={allocDlg} onClose={() => setAllocDlg(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Batch Leave Allocation</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Allocates opening leave balances to all active employees for the selected year.
            Employees already allocated for a type are skipped.
          </Typography>
          <TextField select label="Academic Year" value={allocYear} fullWidth size="small"
            onChange={e => setAllocYear(e.target.value)} InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}>
            {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAllocDlg(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAllocate} disabled={allocating}
            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
            {allocating ? 'Allocating…' : 'Run Allocation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Carry Forward dialog ── */}
      <Dialog open={cfDlg} onClose={() => setCfDlg(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Year-End Carry Forward</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Carries forward unused leave balances for eligible types. Excess over the maximum carry forward cap is forfeited.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField select label="From Year" value={cfFrom} fullWidth size="small"
                onChange={e => setCfFrom(e.target.value)} InputLabelProps={{ shrink: true }}>
                {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select label="To Year" value={cfTo} fullWidth size="small"
                onChange={e => setCfTo(e.target.value)} InputLabelProps={{ shrink: true }}>
                {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCfDlg(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCarryForward} disabled={cfRunning} color="info">
            {cfRunning ? 'Processing…' : 'Run Carry Forward'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Adjustment dialog ── */}
      {discardDialog}
      <Dialog open={adjDlg} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>Manual Leave Adjustment</DialogTitle>
        <DialogContent dividers>
          {adjErr && <Alert severity="error" sx={{ mb: 2 }}>{adjErr}</Alert>}
          <Typography variant="body2" color="text.secondary" mb={2}>
            Use positive days to add and negative days to deduct.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField select label="Employee *" value={adjForm.employeeNo} fullWidth size="small"
                onChange={e => setAdjForm(f => ({ ...f, employeeNo: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}>
                <MenuItem value=""><em>— Select —</em></MenuItem>
                {employees.map(e => (
                  <MenuItem key={e.employeeNo} value={e.employeeNo}>
                    {e.firstName} {e.lastName} ({e.employeeNo})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={8}>
              <TextField select label="Leave Type *" value={adjForm.leaveTypeCode} fullWidth size="small"
                onChange={e => setAdjForm(f => ({ ...f, leaveTypeCode: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}>
                <MenuItem value=""><em>— Select —</em></MenuItem>
                {leaveTypes.map(lt => <MenuItem key={lt.code} value={lt.code}>{lt.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField label="Days *" value={adjForm.days} type="number" fullWidth size="small"
                onChange={e => setAdjForm(f => ({ ...f, days: e.target.value }))}
                helperText="+ve to add, –ve to deduct" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Year *" value={adjForm.academicYear} fullWidth size="small"
                onChange={e => setAdjForm(f => ({ ...f, academicYear: e.target.value }))}
                InputLabelProps={{ shrink: true }}>
                {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Description" value={adjForm.description} fullWidth size="small"
                onChange={e => setAdjForm(f => ({ ...f, description: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleAdjustment} disabled={adjSaving} color="warning">
            {adjSaving ? 'Posting…' : 'Post Adjustment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveLedgerPage;
